import * as React from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface AIContextChipProps {
  /** Eyebrow label (e.g. "Tyndo Copilot"). */
  source?: string;
  /** Headline suggestion. */
  title: React.ReactNode;
  /** Optional secondary detail. */
  detail?: React.ReactNode;
  /** Tone — 'suggestion' (default Iris), 'warning' (alarm-minor), 'critical' (alarm-critical). */
  tone?: 'suggestion' | 'warning' | 'critical';
  /** Action button label (defaults to "Act"). */
  actionLabel?: React.ReactNode;
  onAct?: () => void;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Tyndo AIContextChip — ambient AI hint surfaced inline next to a record
 * (alarm root cause, scheduling suggestion, anomaly flag, deviation hypothesis).
 *
 * The Copilot panel still exists for chat; this is the "I noticed something"
 * counterpart. Tone defaults to suggestion (Iris); escalation tones use
 * alarm tokens so users can scan severity at a glance.
 */
export function AIContextChip({
  source = 'Tyndo Copilot',
  title,
  detail,
  tone = 'suggestion',
  actionLabel = 'Act',
  onAct,
  onDismiss,
  className,
}: AIContextChipProps) {
  const accent =
    tone === 'critical'
      ? 'hsl(var(--alarm-critical))'
      : tone === 'warning'
        ? 'hsl(var(--alarm-minor))'
        : 'var(--color-brand-primary)';

  return (
    <div
      className={cn(
        'group relative flex items-start gap-3 overflow-hidden rounded-lg border border-border bg-surface-raised p-3',
        className
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-0.5"
        style={{ backgroundColor: accent }}
      />
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
        style={{
          backgroundColor: `${accent.slice(0, -1)} / 0.12)`,
          color: accent,
        }}
      >
        <Sparkles className="h-3.5 w-3.5" />
      </span>
      <div className="flex-1 min-w-0 space-y-0.5">
        <p
          className="t-eyebrow"
          style={{ color: accent }}
        >
          {source}
        </p>
        <p className="text-sm font-medium text-foreground leading-snug">
          {title}
        </p>
        {detail && (
          <p className="text-xs text-muted-foreground leading-snug">{detail}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {onAct && (
          <button
            type="button"
            onClick={onAct}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors"
            style={{
              backgroundColor: `${accent.slice(0, -1)} / 0.12)`,
              color: accent,
            }}
          >
            {actionLabel}
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded p-1 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
